/**
 * Browser refresh loop for the homepage market rows (canideal.org
 * assets/homepage/market-data.js `start`). One request at a time; a response
 * that arrives after stop() or after a newer request is ignored; polling pauses
 * while the tab is hidden and resumes on visibility; records expire on time even
 * without a successful fetch.
 */
import {
  MARKET_ROW_INTERVAL,
  MARKET_ROW_TIMEOUT,
  MARKET_ROW_WARMUP_DELAY,
  MARKET_ROW_WARMUP_RETRIES,
  MARKET_ROW_SPECS,
  expireMarketRows,
  marketRowsStatus,
  nextMarketRowExpiry,
  proofTimestamp,
  reconcileMarketRows,
} from '@/lib/marketRows';
import type { MarketRowsState } from '@/lib/marketRows';

export interface MarketRowsView {
  state: MarketRowsState;
  status: string;
}

export interface MarketRowsPollerDeps {
  /** Reads the Terminal feed; rejects with Error('invalid_feed') for an unusable body. */
  read: (signal: AbortSignal) => Promise<{ ok: boolean; payload: unknown }>;
  onChange: (view: MarketRowsView) => void;
  now?: () => number;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  isVisible?: () => boolean;
}

export interface MarketRowsPoller {
  refresh: () => Promise<void>;
  /** Call on `visibilitychange`. */
  visibility: () => void;
  stop: () => void;
  getState: () => MarketRowsState;
}

export function startMarketRowsPoller(
  initial: MarketRowsState,
  {
    read,
    onChange,
    now = () => Date.now(),
    setTimer = (callback, ms) => setTimeout(callback, ms),
    clearTimer = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  }: MarketRowsPollerDeps
): MarketRowsPoller {
  let state = initial;
  let inFlight = false;
  let poll: unknown = null;
  let lastAttempt = -Infinity;
  let transportDelayed = false;
  let stopped = false;
  let sequence = 0;
  let completedOnce = MARKET_ROW_SPECS.every((spec) => state[spec.key].record);
  let warmupRetries = 0;
  let warmupRetryAt = Infinity;

  function render() {
    state = expireMarketRows(state, now());
    onChange({
      state,
      status: marketRowsStatus(state, {
        transportDelayed,
        warmingUp: Number.isFinite(warmupRetryAt),
      }),
    });
  }

  function schedule() {
    if (poll !== null) {
      clearTimer(poll);
      poll = null;
    }
    if (stopped || !isVisible()) return;
    const due = Math.min(
      lastAttempt + MARKET_ROW_INTERVAL,
      warmupRetryAt,
      nextMarketRowExpiry(state)
    );
    poll = setTimer(tick, Math.max(1, due - now()));
  }

  async function refresh() {
    if (stopped || inFlight || !isVisible()) return;
    inFlight = true;
    lastAttempt = now();
    warmupRetryAt = Infinity;
    const request = ++sequence;
    const controller = new AbortController();
    let timeout: unknown = null;
    try {
      const deadline = new Promise<never>((_, reject) => {
        timeout = setTimer(() => {
          controller.abort();
          reject(new Error('transport_timeout'));
        }, MARKET_ROW_TIMEOUT);
      });
      const response = await Promise.race([read(controller.signal), deadline]);
      const payload = response.payload as {
        status?: unknown;
        signals?: unknown;
        checked_at?: unknown;
      };
      if (!response.ok && payload?.status !== 'unavailable')
        throw new Error('transport_unavailable');
      if (stopped || request !== sequence) return;
      state = reconcileMarketRows(state, payload, now());
      transportDelayed = false;
      const complete = MARKET_ROW_SPECS.every((spec) => state[spec.key].record);
      if (complete) completedOnce = true;
      else if (
        !completedOnce &&
        payload?.status === 'partial' &&
        Array.isArray(payload.signals) &&
        Number.isFinite(proofTimestamp(payload.checked_at)) &&
        warmupRetries < MARKET_ROW_WARMUP_RETRIES
      ) {
        warmupRetries += 1;
        warmupRetryAt = now() + MARKET_ROW_WARMUP_DELAY;
      }
    } catch (error) {
      if (stopped || request !== sequence) return;
      if ((error as Error)?.message === 'invalid_feed') {
        state = reconcileMarketRows(state, null, now());
        transportDelayed = false;
      } else {
        state = expireMarketRows(state, now());
        transportDelayed = true;
      }
    } finally {
      if (timeout !== null) clearTimer(timeout);
      if (request === sequence) {
        inFlight = false;
        if (!stopped) {
          render();
          schedule();
        }
      }
    }
  }

  function tick() {
    poll = null;
    render();
    if (now() >= warmupRetryAt || now() - lastAttempt >= MARKET_ROW_INTERVAL) void refresh();
    else schedule();
  }

  function visibility() {
    render();
    if (isVisible() && (now() >= warmupRetryAt || now() - lastAttempt >= MARKET_ROW_INTERVAL))
      void refresh();
    else schedule();
  }

  return {
    refresh,
    visibility,
    getState: () => state,
    stop() {
      stopped = true;
      sequence += 1;
      if (poll !== null) clearTimer(poll);
    },
  };
}
